import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  Users, CheckCircle2, XCircle, Wifi, Navigation, Search,
  ChevronRight, UserCheck, UserX, Calendar, Clock, MapPin,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FrequenciaPage() {
  const { currentBranch } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [searchPeople, setSearchPeople] = useState("");
  const [allPeople, setAllPeople] = useState([]);
  const [addAttendanceDialog, setAddAttendanceDialog] = useState(false);
  const [filterEvento, setFilterEvento] = useState("");

  useEffect(() => {
    loadEvents();
  }, [currentBranch]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      const res = await axios.get(`${API}/events${filialParam}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Erro ao carregar eventos");
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = useCallback(async (eventId) => {
    try {
      setLoadingAttendance(true);
      const [attendanceRes, statsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/attendance`),
        axios.get(`${API}/events/${eventId}/attendance/stats`)
      ]);
      setAttendance(attendanceRes.data);
      setAttendanceStats(statsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar frequência");
    } finally {
      setLoadingAttendance(false);
    }
  }, []);

  const loadPeople = async () => {
    try {
      const res = await axios.get(`${API}/people?limit=500`);
      setAllPeople(res.data);
    } catch (error) {
      console.error("Error loading people:", error);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    loadAttendance(event.id);
  };

  const handleMarkAttendance = async (pessoaId, presente) => {
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/attendance`, {
        pessoa_id: pessoaId,
        evento_id: selectedEvent.id,
        presente
      });
      toast.success(presente ? "Presença registrada" : "Ausência registrada");
      loadAttendance(selectedEvent.id);
    } catch (error) {
      toast.error("Erro ao registrar");
    }
  };

  const handleAddManualAttendance = async (pessoa) => {
    await handleMarkAttendance(pessoa.id, true);
    setAddAttendanceDialog(false);
    setSearchPeople("");
  };

  const getEventStatus = (event) => {
    const now = new Date();
    const start = event.data_inicio ? parseISO(event.data_inicio) : null;
    const end = event.data_fim ? parseISO(event.data_fim) : null;
    
    if (!start || !end) return "pendente";
    if (now > end) return "encerrado";
    if (now < start) return "agendado";
    return "em_andamento";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "em_andamento":
        return <Badge className="bg-green-500">Em andamento</Badge>;
      case "agendado":
        return <Badge variant="outline">Agendado</Badge>;
      case "encerrado":
        return <Badge variant="secondary">Encerrado</Badge>;
      default:
        return <Badge variant="outline">Pendente</Badge>;
    }
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return "--";
    try {
      const date = parseISO(dateStr);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const filteredPeople = allPeople.filter(p => {
    if (!searchPeople) return true;
    const search = searchPeople.toLowerCase();
    return p.nome_completo?.toLowerCase().includes(search) ||
           p.telefone?.includes(search);
  });

  const filteredEvents = events.filter(e => {
    if (!filterEvento) return true;
    const titulo = (e.titulo || e.nome || "").toLowerCase();
    return titulo.includes(filterEvento.toLowerCase());
  });

  // Pessoas que já estão na lista de frequência
  const attendancePersonIds = new Set(attendance.map(a => a.pessoa_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="frequencia-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Frequência</h1>
        {selectedEvent && (
          <Button variant="outline" size="sm" onClick={() => loadAttendance(selectedEvent.id)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lista de Eventos */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Eventos</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar evento..."
                value={filterEvento}
                onChange={(e) => setFilterEvento(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-2">
                {filteredEvents.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nenhum evento encontrado</p>
                ) : (
                  filteredEvents.map(event => {
                    const titulo = event.titulo || event.nome;
                    const isSelected = selectedEvent?.id === event.id;
                    const status = getEventStatus(event);
                    
                    return (
                      <Button
                        key={event.id}
                        variant={isSelected ? "default" : "ghost"}
                        className={`w-full justify-start h-auto py-3 px-3 ${isSelected ? "" : "hover:bg-muted"}`}
                        onClick={() => handleSelectEvent(event)}
                      >
                        <div className="flex flex-col items-start gap-1 w-full">
                          <div className="flex items-center gap-2 w-full">
                            <span className="font-medium truncate">{titulo}</span>
                            {status === "em_andamento" && (
                              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs opacity-70">
                            <Calendar className="h-3 w-3" />
                            {event.data_inicio ? format(parseISO(event.data_inicio), "dd/MM", { locale: ptBR }) : "--"}
                            <Clock className="h-3 w-3 ml-1" />
                            {event.data_inicio ? format(parseISO(event.data_inicio), "HH:mm", { locale: ptBR }) : "--"}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                      </Button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Detalhes da Frequência */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedEvent ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center">
                  <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">Selecione um Evento</h3>
                  <p className="text-muted-foreground">
                    Escolha um evento na lista ao lado para visualizar a frequência
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Event Header */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 flex-wrap">
                        {selectedEvent.titulo || selectedEvent.nome}
                        {getStatusBadge(getEventStatus(selectedEvent))}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        <span className="flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatEventDate(selectedEvent.data_inicio)}
                          </span>
                          {selectedEvent.local && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {selectedEvent.local}
                            </span>
                          )}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Stats Cards */}
              {attendanceStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-green-100">
                          <UserCheck className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{attendanceStats.total_presentes}</p>
                          <p className="text-xs text-muted-foreground">Presentes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-red-100">
                          <UserX className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{attendanceStats.total_ausentes}</p>
                          <p className="text-xs text-muted-foreground">Ausentes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-blue-100">
                          <Navigation className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{attendanceStats.por_metodo?.gps || 0}</p>
                          <p className="text-xs text-muted-foreground">Via GPS</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-purple-100">
                          <Wifi className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{attendanceStats.por_metodo?.wifi || 0}</p>
                          <p className="text-xs text-muted-foreground">Via WiFi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Attendance List */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Lista de Frequência</CardTitle>
                    <Button size="sm" onClick={() => { loadPeople(); setAddAttendanceDialog(true); }}>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Adicionar Manual
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingAttendance ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : attendance.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">Nenhuma presença registrada</p>
                      <p className="text-sm mt-1">As presenças são registradas automaticamente via GPS ou WiFi</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {attendance.map((record) => (
                        <div 
                          key={record.id} 
                          className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              record.presente ? "bg-green-100" : "bg-red-100"
                            }`}>
                              {record.presente ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">
                                {record.pessoa?.nome_completo || "Pessoa não encontrada"}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="outline" className="text-xs gap-1">
                                  {record.metodo_confirmacao === "gps" && <Navigation className="h-3 w-3" />}
                                  {record.metodo_confirmacao === "wifi" && <Wifi className="h-3 w-3" />}
                                  {record.metodo_confirmacao === "manual" && <UserCheck className="h-3 w-3" />}
                                  {record.metodo_confirmacao || "manual"}
                                </Badge>
                                {record.confirmado_em && (
                                  <span>
                                    {format(parseISO(record.confirmado_em), "HH:mm", { locale: ptBR })}
                                  </span>
                                )}
                                {record.verificacoes > 1 && (
                                  <span className="text-muted-foreground">
                                    ({record.verificacoes}x verificado)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            {!record.presente ? (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleMarkAttendance(record.pessoa_id, true)}
                                title="Marcar como presente"
                              >
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleMarkAttendance(record.pessoa_id, false)}
                                title="Marcar como ausente"
                              >
                                <XCircle className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* ===== ADD MANUAL ATTENDANCE DIALOG ===== */}
      <Dialog open={addAttendanceDialog} onOpenChange={setAddAttendanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Presença Manual</DialogTitle>
            <DialogDescription>
              Busque e selecione a pessoa para registrar presença em "{selectedEvent?.titulo || selectedEvent?.nome}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou telefone..."
                value={searchPeople}
                onChange={(e) => setSearchPeople(e.target.value)}
                className="pl-10"
              />
            </div>

            <ScrollArea className="h-[300px] border rounded-md">
              <div className="p-2 space-y-1">
                {filteredPeople
                  .filter(p => !attendancePersonIds.has(p.id))
                  .slice(0, 50)
                  .map(pessoa => (
                    <Button
                      key={pessoa.id}
                      variant="ghost"
                      className="w-full justify-start h-auto py-2"
                      onClick={() => handleAddManualAttendance(pessoa)}
                    >
                      <UserCheck className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                      <div className="flex flex-col items-start">
                        <span className="truncate">{pessoa.nome_completo}</span>
                        {pessoa.telefone && (
                          <span className="text-xs text-muted-foreground">{pessoa.telefone}</span>
                        )}
                      </div>
                    </Button>
                  ))}
                {filteredPeople.filter(p => !attendancePersonIds.has(p.id)).length === 0 && (
                  <p className="text-center text-muted-foreground py-4">
                    {searchPeople ? "Nenhuma pessoa encontrada" : "Todas as pessoas já foram adicionadas"}
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
